/* eslint-disable jsx-a11y/media-has-caption */
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { makeGet, makePost } from "@culturehq/client";
import { ActionButton, Button, Icon, Loader, ModalDialog, PlainButton, StringField } from "@culturehq/components";

import linkedin from "images/social/linkedin_white.svg";

import { useTryCatch } from "app/hooks";
import { useProfile } from "app/context";
import LinkedinLink from "app/links/LinkedinLink";
import { getImageFromStory, t, Trans } from "app/utils";

import imagesSizes from "constants/imagesSizes";

import CommentsList from "components/comments/CommentsList";
import ErrorMessage from "components/common/ErrorMessage";
import LightboxImageSlider from "components/common/gallery/LightboxImageSlider";
import Subtitles from "components/common/Subtitles";
import StoryShareLinkedin from "components/linkedin/StoryShareLinkedin";

import { cloneDeep } from "lodash";
import ChqModalDialog from "components/common/ChqModalDialog";
import LinkedinPostCreator from "./LinkedinPostCreator";
import LinkedinReaction from "./LinkedinReaction";
import LinkedinReactionIcon from "./LinkedinReactionIcon";
import LinkedinReactionText from "./LinkedinReactionText";
import LinkedinReactionsSummary from "./LinkedinReactionsSummary";
import LinkedinMentionText from "./LinkedinMentionText";

const EngageLinkedin = ({
  children, story, linkedinShare, topModal = false, updateLinkedinShare, displayModalFromUrl
}) => {
  const mediaRef = React.createRef();
  const lazyLoadingContainerRef = React.useRef();
  const { profile } = useProfile();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showVideoInfo, setShowVideoInfo] = useState(false);
  const [myReaction, setMyReaction] = useState();
  const [reactions, setReactions] = useState([]);
  const [comments, setComments] = useState([]);
  const [totalCommentsCount, setTotalCommentsCount] = useState(0);
  const [nextPage, setNextPage] = useState();
  const [loading, setLoading] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [comment, setComment] = useState("");
  const [loadingSocialActions, setLoadingSocialActions] = useState(false);
  const [commentKey, setCommentKey] = useState(0);
  const [firstRender, setFirstRender] = useState(true);


  const [{ error, success, trying, response }, onReacted] = useTryCatch(
    async ({ reaction, commentUrn, deleting, linkedinShare }) => {
      const response = await makePost(`/linkedin/posts/${linkedinShare.id}/reactions`, { reaction, commentUrn, deleting });
      await getLinkedinComments().then(processLinkedinComments);
      return response;
    }
  );

  const authenticateUser = async () => {
    let locationUrl;
    if (location.pathname.startsWith("/stories/trend")) {
      locationUrl = `${location.pathname}/story/${story.id}/engage`;
    } else {
      locationUrl = `/stories/${story.id}/engage`;
    }
    const { url } = await makeGet("/linkedin/integration", { url: locationUrl });
    window.location.href = url;
  };

  if (error && error.status === 403) {
    authenticateUser();
  }

  const getLinkedinComments = async currentNextPage => {
    setLoadingComments(true);
    return makeGet(`/linkedin/posts/${linkedinShare.id}/comments`, { nextPage: currentNextPage });
  };

  const getLinkedinReactions = async () => makeGet(`/linkedin/posts/${linkedinShare.id}/reactions`);

  const processLinkedinComments = ({
    comments: linkedinComments,
    nextPage,
    totalCommentsCount
  }, append) => {
    const newComments = append ? [
      ...cloneDeep(comments),
      ...linkedinComments
    ] : linkedinComments;

    setComments(newComments);
    setNextPage(nextPage);
    if (totalCommentsCount) {
      setTotalCommentsCount(totalCommentsCount);
      if (updateLinkedinShare) {
        updateLinkedinShare({ ...linkedinShare, comments: totalCommentsCount });
      }
    }
    setLoadingComments(false);
  };

  useEffect(
    () => {
      let isMounted = true;

      const getLinkedinSocialActions = async () => {
        const { socialShare: share, myReaction: reaction } = await makeGet(`/linkedin/posts/${linkedinShare.id}`);
        setReactions(share.linkedinReactionsV2);
        setMyReaction(reaction);
        if (updateLinkedinShare) {
          updateLinkedinShare({
            ...linkedinShare,
            reactions: share.linkedinReactionsV2.total
          });
        }
        setLoadingSocialActions(false);
      };


      if (open) {
        setLoadingSocialActions(true);
        getLinkedinSocialActions();
        getLinkedinComments().then(res => isMounted && processLinkedinComments(res));
        getLinkedinReactions().then();
      }

      return () => {
        isMounted = false;
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedinShare.id, open]
  );

  useEffect(
    () => {
      const paths = location.pathname.split("/");
      if (paths[paths.length - 1] === "share" && profile?.userLinkedin?.linkedinId
        && `${story.id}` === paths[paths.length - 2] && displayModalFromUrl) {
        setOpen(true);
      }
      if (paths[paths.length - 1] === "engage" && profile?.userLinkedin?.linkedinId
        && `${story.id}` === paths[paths.length - 2] && displayModalFromUrl) {
        setOpen(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  );

  useEffect(
    () => {
      if (success && response?.socialShare) {
        const { socialShare: updatedLinkedinShare, myReaction: reaction } = response;
        setReactions(updatedLinkedinShare.linkedinReactionsV2);
        setMyReaction(reaction);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [success, trying]
  );

  const onOpen = useCallback(
    () => {
      const authenticateUser = async () => {
        let locationUrl;
        if (location.pathname.startsWith("/stories/trend")) {
          locationUrl = `${location.pathname}/story/${story.id}/engage`;
        } else {
          locationUrl = `/stories/${story.id}/engage`;
        }
        const { url } = await makeGet("/linkedin/integration", { url: locationUrl });
        window.location.href = url;
      };

      if (!profile?.userLinkedin?.linkedinId) {
        authenticateUser();
        return;
      }

      setOpen(true);
    },
    [setOpen, profile, location.pathname, story.id]
  );

  const onClose = useCallback(
    () => {
      setOpen(false);
    },
    [setOpen]
  );

  const onPostSocialComment = async (newComment, parentComment = undefined) => {
    if (newComment.trim() !== "") {
      try {
        setLoadingComments(true);
        await makePost(
          `/linkedin/posts/${linkedinShare.id}/comments`,
          {
            comment: newComment.trim(),
            parentComment
          }
        );
        await getLinkedinComments().then(processLinkedinComments);
      } catch (error) {
        if (error.status === 403) {
          authenticateUser();
        }
        setLoading(false);
      }
    }
  };

  const onPostComment = async () => {
    await onPostSocialComment(comment);
    setComment("");
  };

  const handlePlay = () => {
    mediaRef.current.play();
  };

  const handleCommentChange = value => {
    setComment(value);
  };

  const handleOnShared = () => {
    setOpen(false);
  };

  const handleAppend = () => nextPage && getLinkedinComments(nextPage).then(
    res => processLinkedinComments(res, true));

  const imageUrl = story.media.mediaType === "image" ? getImageFromStory(imagesSizes.NORMAL, story) : story.media.thumbnail;

  const handleFocusCommentInput = () => {
    setFirstRender(false);
    setCommentKey(prev => (prev ? 0 : 1));
  };

  return (
    <>
      {children && (
        <div onClick={onOpen} role="button" tabIndex={0}>
          {children}
        </div>
      )}
      {topModal && !children && (
        <div className="gallery-lightbox__header gallery-lightbox__header--Linkedin" role="button" onClick={onOpen} tabIndex={0}>
          <span className="legend">
            <Icon icon="thumbsup" />
            {" "}
            <Trans>Click here to engage with this LinkedIn post</Trans>
          </span>
        </div>
      )}
      {!topModal && !children && (
        <ActionButton aria-label="Share" icon="thumbsup" onClick={onOpen}>
          <Trans>Engage</Trans>
        </ActionButton>
      )}
      {open && (
        <ChqModalDialog onClose={onClose} entrance="zoomIn" className="LinkedinEngageModal gallery-lightbox gallery-lightbox--Story">
          <ModalDialog.Heading onClose={onClose}>
            <img className="social-icon" src={linkedin} alt="LinkedIn icon" />{" "}
            <Trans>Engage with this story from LinkedIn</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <div className="Story-Image">
              <div className="gallery-lightbox__wrapper">
                <div className="gallery-lightbox__photo col-sm-7 col-xs-12">
                  <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${imageUrl})` }} />
                  {story.media.mediaType === "image" && (
                    <LightboxImageSlider
                      boxImages={story.visuals}
                      onImageLoad={() => {}}
                    />
                  )}
                  {story.media.mediaType === "video" && (
                    <>
                      {showVideoInfo && (
                        <div className="gallery-lightbox__video-play" onClick={handlePlay}><Icon icon="play-filled" /></div>
                      )}
                      <video
                        autoPlay
                        controls
                        className="gallery-lightbox__main-image gallery-lightbox__main-video"
                        onPause={() => setShowVideoInfo(true)}
                        onPlay={() => setShowVideoInfo(false)}
                        poster={`${story.media.thumbnail}?not-from-cache-please`}
                        ref={mediaRef}
                        src={story.media.url}
                        disablePictureInPicture
                        crossOrigin="anonymous"
                        controlsList="nodownload"
                      >
                        <Subtitles media={story.media} />
                      </video>
                    </>
                  )}
                </div>
                <div className="gallery-lightbox__details-container col-sm-5 col-xs-12">
                  {loadingSocialActions ? (
                    <Loader loading />
                  ) : (
                    <>
                      <div ref={lazyLoadingContainerRef} className="scrollable-container no-scrollbar">
                        <LinkedinPostCreator linkedinShare={linkedinShare} />
                        <p className="share-message"><LinkedinMentionText value={linkedinShare.message} /></p>
                        <CommentsList
                          lazyLoadingContainerRef={lazyLoadingContainerRef}
                          comments={comments}
                          profile={profile}
                          onPostSocialComment={onPostSocialComment}
                          onReacted={onReacted}
                          linkedinShare={linkedinShare}
                          onToggleShowMore={handleAppend}
                          loading={loadingComments}
                        />
                      </div>
                      <div className="Interactions-Container">
                        <LinkedinReactionsSummary
                          comments={comments}
                          totalCommentsCount={totalCommentsCount}
                          reactions={reactions}
                        />
                        <LinkedinLink linkedinId={linkedinShare.externalId}>
                          <Trans>View this post on LinkedIn</Trans>
                        </LinkedinLink>
                      </div>
                      <hr />
                      <div className="Linkedin-Actions">
                        <LinkedinReaction
                          onReacted={onReacted}
                          myReaction={myReaction}
                          linkedinShare={linkedinShare}
                        >
                          <LinkedinReactionIcon reaction={myReaction} />
                          <LinkedinReactionText reaction={myReaction} />
                        </LinkedinReaction>
                        <PlainButton onClick={handleFocusCommentInput}>
                          <Icon icon="linkedin-comment" />
                          <Trans>Comment</Trans>
                        </PlainButton>
                        <StoryShareLinkedin
                          story={story}
                          linkedinShare={linkedinShare}
                          onShared={handleOnShared}
                        />
                      </div>
                      <div className="Linkedin-Comments">
                        <StringField
                          key={commentKey}
                          onChange={handleCommentChange}
                          placeholder={t("Add a comment...")}
                          value={comment}
                          autoFocus={!firstRender}
                        />
                        <Button onClick={onPostComment}>
                          <Trans>Post</Trans>
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            <Loader loading={loading || trying} />
            {error && <ErrorMessage error={error} />}
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default EngageLinkedin;
